import { formatBytes } from '@jsonfmt/core';

import type { DocumentStore, ReplaceOutcome } from '@/features/document/documentStore';
import type { Command, CommandContext, CommandResult, Notice } from '@/features/types';
import {
  toastHub as defaultHub,
  type ToastHub,
  type ToastInput,
} from '@/ui/notifications/toastHub';

import { TOAST_DEFAULT_DURATION_MS, TOAST_REPAIR_DURATION_MS } from './config';
import type { Translate } from './commandContext';

/**
 * THE SINGLE PLACE A `CommandResult` BECOMES EFFECTS — docs/ARCHITECTURE.md §4.9, §5.2,
 * ADR-0014.
 *
 * A command returns data and performs nothing. This file is the other half of that bargain:
 * every toolbar click, every keyboard shortcut, every row's Fix button and every button on the
 * empty and blocked screens funnels through `runCommand`, so there is exactly one ordering of
 * effects in the product and exactly one `try/catch` around a command.
 *
 * ═══ THE ORDER IS LOAD-BEARING, NOT ARBITRARY ═══
 *
 *   1. the document  — `store.replace` / `store.applyEdits`
 *   2. the toast     — at most one, `Notice.key` translated here at the boundary
 *   3. the reveal    — `ctx.ui.revealSpan`
 *   4. the dialogs   — `closeDialogs`
 *
 * The document goes FIRST because the reveal describes it. Paste's `reveal` is an offset into
 * the text being inserted, and `applyFixAt`'s is a collapsed caret whose offsets describe the
 * text BEFORE its edits; revealing before applying would scroll the old buffer to a position
 * that is about to move. Two commands depend on this and a test pins each.
 *
 * ═══ THE STORE'S REFUSAL OUTRANKS THE COMMAND'S NOTICE ═══
 *
 * `store.replace` returns `{ ok: false, reason: 'too-large' }` above the hard limit and leaves
 * the previous document untouched (ADR-0006 rule 2 — the named regression). Nothing in
 * `src/features/**` knows that limit: `config.ts` is the composition root and a feature may
 * not import it, so no command can emit `toast.error.tooLarge` and this file is the only place
 * that can. When the store refuses, the command's own notice is DISCARDED — a success toast
 * over a document that did not change is the lie this ordering exists to prevent — and the
 * reveal and `closeDialogs` are skipped with it, because nothing happened.
 *
 * ═══ A THROW IS ONE ERROR TOAST AND NOTHING ELSE ═══
 *
 * `store.applyEdits` throws `OverlappingEditsError` on edits that are not applicable, the
 * clipboard can reject, the analyzer can fail. All of it lands as `toast.error.unexpected` plus
 * a `console.error`, which is the entire reporting strategy of this application (ADR-0023: no
 * telemetry, no SDK, nothing leaves the page). Nothing propagates to a React render, so a
 * failed command can never blank the screen.
 */

export interface RunCommandDeps {
  /** The toast store. Defaults to the singleton; tests pass their own. */
  readonly hub?: ToastHub | undefined;
  /** `CommandResult.closeDialogs`. `App` closes the examples dialog with it. */
  readonly closeDialogs?: (() => void) | undefined;
  /**
   * `HARD_LIMIT_BYTES`, for the refusal sentence only. The store enforces the limit itself;
   * this is the number the toast prints.
   */
  readonly hardLimitBytes?: number | undefined;
  /** Where a thrown command goes in addition to the toast. Defaults to `console.error`. */
  readonly onError?: ((error: unknown) => void) | undefined;
  /**
   * Observes each `Notice` on its way to the toast, BEFORE it is translated. In addition to
   * the toast, never instead of it.
   *
   * It exists for one screen: the auto-repair banner of docs/DESIGN.md §6.5, which is
   * persistent while the toast is transient and which therefore needs to see the same event.
   * The observer keys off `Notice.key` — a real, gated contract, since every key is checked by
   * `npm run i18n:check` — rather than off a toast id, which is a private string a feature may
   * rename at will.
   */
  readonly onNotice?: ((notice: Notice) => void) | undefined;
}

/**
 * Runs one command and applies its result.
 *
 * ARCHITECTURE §4.9 declares this `: void`. It returns `Promise<void>` instead, which is
 * call-compatible everywhere a `void` was expected (`void runCommand(…)` at every call site)
 * and is the difference between a test that awaits the effects and a test that sleeps. It
 * NEVER rejects: the `catch` is the point of the function.
 */
export async function runCommand(
  command: Command,
  ctx: CommandContext,
  store: DocumentStore,
  deps: RunCommandDeps = {},
): Promise<void> {
  try {
    // Read the snapshot ONCE, and hand the same one to `run`. Re-reading after an `await`
    // would give a command a document it never saw — the analyzer may have landed a report,
    // or the user may have typed — and `CommandResult.reveal` would describe neither.
    const snapshot = store.getSnapshot();
    const result = await command.run(ctx, snapshot);
    applyResult(result, ctx, store, deps);
  } catch (error) {
    (deps.onError ?? defaultOnError)(error);
    show(deps.hub ?? defaultHub, ctx.t, {
      tone: 'error',
      key: 'toast.error.unexpected',
      id: `${command.id}.error`,
    });
  }
}

function defaultOnError(error: unknown): void {
  // The whole of error reporting. See the file header.
  console.error('runCommand: command failed', error);
}

/**
 * The four effects, in order. Exported so `App` can apply a `FeatureApi` write through exactly
 * the same path a command takes — a panel writing to the store by itself would be a second
 * ordering of the same four steps, which is how they drift.
 */
export function applyResult(
  result: CommandResult,
  ctx: CommandContext,
  store: DocumentStore,
  deps: RunCommandDeps = {},
): void {
  const hub = deps.hub ?? defaultHub;

  // ── 1. the document ────────────────────────────────────────────────────────────────────
  const outcome = writeDocument(result, store);
  if (outcome !== null && !outcome.ok) {
    showTooLarge(hub, ctx.t, outcome, deps.hardLimitBytes);
    return;
  }

  // ── 2. at most one toast, and none at all when a banner already says it ────────────────
  //
  // A notice that carries `repair` is shown by the PERSISTENT surfaces and only by them: the
  // §6.5 banner (through `onNotice`) and, since ADR-0101 §7.15, the Repair log behind the
  // status door. Both list the repairs one by one and both stay until the user dismisses them.
  // The toast said the same sentence a second time, two inches below, and then took itself
  // away — so the recon read "the banner and the toast repeat the same fix list" as the single
  // loudest piece of noise in the product (ADR-0101 §7.10).
  //
  // `onNotice` still fires. The banner is what consumes it, so suppressing that would suppress
  // the surface this rule exists to protect; it is only the transient copy that goes.
  if (result.notice !== undefined) {
    deps.onNotice?.(result.notice);
    if (result.notice.repair === undefined) show(hub, ctx.t, result.notice);
  }

  // ── 3. the reveal ──────────────────────────────────────────────────────────────────────
  if (result.reveal !== undefined) ctx.ui.revealSpan(result.reveal);

  // ── 4. the dialogs ─────────────────────────────────────────────────────────────────────
  if (result.closeDialogs === true) deps.closeDialogs?.();
}

/**
 * `null` when the result carries no document change at all.
 *
 * `document` and `edits` are mutually exclusive by convention rather than by type, and
 * `document` wins if both appear: a whole-text replacement is the stronger statement, and
 * applying both would be two version bumps, therefore two undo steps, for one action.
 */
function writeDocument(result: CommandResult, store: DocumentStore): ReplaceOutcome | null {
  if (result.document !== undefined) return store.replace(result.document.text);
  if (result.edits !== undefined && result.edits.length > 0) return store.applyEdits(result.edits);
  return null;
}

/**
 * A repair notice lists what changed, item by item, so it is given twice as long to read.
 *
 * Keyed off the same `toast.repair.` prefix `App.tsx` keys the persistent §6.5 banner off,
 * for the same reason it gives: `Notice.key` is a real contract that `npm run i18n:check`
 * gates, where a toast id is a private string a feature may rename at will.
 */
function durationFor(notice: Notice): number {
  if (notice.durationMs !== undefined) return notice.durationMs;
  return notice.key.startsWith('toast.repair.')
    ? TOAST_REPAIR_DURATION_MS
    : TOAST_DEFAULT_DURATION_MS;
}

/** `Notice` → `ToastInput`: this is where an i18n KEY becomes a SENTENCE (ADR-0018). */
export function show(hub: ToastHub, t: Translate, notice: Notice): void {
  const input: ToastInput = {
    tone: notice.tone,
    message: t(notice.key, notice.params),
    ...(notice.id === undefined ? {} : { id: notice.id }),
    durationMs: durationFor(notice),
    ...(notice.action === undefined
      ? {}
      : { action: { label: t(notice.action.labelKey), onInvoke: notice.action.onInvoke } }),
  };
  hub.show(input);
}

/**
 * The one sentence no command can produce, because no command knows the limit.
 *
 * A fixed id, so a user hammering Paste against a 30 MB clipboard replaces one toast rather
 * than stacking three.
 */
export function showTooLarge(
  hub: ToastHub,
  t: Translate,
  outcome: Extract<ReplaceOutcome, { ok: false }>,
  hardLimitBytes?: number,
): void {
  show(hub, t, {
    tone: 'error',
    key: 'toast.error.tooLarge',
    params: {
      size: formatBytes(outcome.bytes),
      limit: formatBytes(hardLimitBytes ?? outcome.limit),
    },
    id: 'document.too-large',
  });
}
